import { useState, useRef, useEffect, useMemo } from "react";
import { Play, Pause, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * VideoCanvas — HTML5 video player with timestamp-anchored annotation pins.
 *
 * Annotations are pins with:
 *   - x, y: percent within the video frame (0-100)
 *   - t:    timestamp in seconds (float)
 *
 * Props:
 *   fileUrl, comments, readOnly, activeTool, onCreateAnnotation, selectedCommentId, setSelectedCommentId, testidPrefix
 */
export default function VideoCanvas({
  fileUrl,
  comments,
  readOnly = false,
  activeTool = "pin",
  onCreateAnnotation,
  selectedCommentId,
  setSelectedCommentId,
  testidPrefix = "video-anno",
  pendingAnno = null,
  reviewItemId = null,
}) {
  const videoRef = useRef(null);
  const wrapRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const rateStorageKey = reviewItemId ? `co_video_rate_${reviewItemId}` : "co_video_rate";
  const [rate, setRate] = useState(() => {
    if (typeof window === "undefined") return 1;
    const v = parseFloat(localStorage.getItem(rateStorageKey) || "1");
    return isNaN(v) ? 1 : v;
  });

  // Tolerance — comments within ±0.6s of currentTime are considered "live"
  const TOL = 0.6;

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onMeta = () => { setDuration(v.duration || 0); v.playbackRate = rate; };
    const onTime = () => setCurrentTime(v.currentTime || 0);
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    v.addEventListener("loadedmetadata", onMeta);
    v.addEventListener("timeupdate", onTime);
    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    return () => {
      v.removeEventListener("loadedmetadata", onMeta);
      v.removeEventListener("timeupdate", onTime);
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
    };
  }, [fileUrl, rate]);

  // Sync rate to element + localStorage whenever changed
  useEffect(() => {
    if (videoRef.current) videoRef.current.playbackRate = rate;
    try { localStorage.setItem(rateStorageKey, String(rate)); } catch { /* ignore */ }
  }, [rate, rateStorageKey]);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play(); else v.pause();
  };

  const seekTo = (t) => {
    const v = videoRef.current;
    if (!v) return;
    v.pause();
    v.currentTime = Math.max(0, Math.min(t, v.duration || 0));
  };

  const handleVideoClick = (e) => {
    if (readOnly || activeTool !== "pin") return;
    e.preventDefault();
    e.stopPropagation();
    const wrap = wrapRef.current;
    if (!wrap) return;
    const rect = wrap.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    if (x < 0 || x > 100 || y < 0 || y > 100) return;
    // Pause to capture an exact timestamp
    if (videoRef.current && !videoRef.current.paused) videoRef.current.pause();
    onCreateAnnotation && onCreateAnnotation({
      type: "pin",
      x,
      y,
      t: Math.round((videoRef.current?.currentTime || 0) * 10) / 10,
    });
  };

  const pinComments = useMemo(
    () => comments.filter((c) => c.annotation?.type === "pin" && typeof c.annotation.t === "number"),
    [comments]
  );
  const livePins = pinComments.filter((c) => Math.abs((c.annotation.t || 0) - currentTime) <= TOL);

  return (
    <div className="w-full" data-testid="video-canvas-root">
      <div
        ref={wrapRef}
        className={`relative inline-block bg-black ${readOnly ? "" : "cursor-crosshair"}`}
        style={{ width: "min(100%, 1000px)" }}
        onClick={handleVideoClick}
        data-testid={`${testidPrefix}-frame`}
      >
        <video
          ref={videoRef}
          src={fileUrl}
          className="w-full h-auto block pointer-events-none"
          controls={false}
          preload="metadata"
          data-testid="video-player"
        />
        {livePins.map((c) => {
          const num = comments.indexOf(c) + 1;
          const isSel = selectedCommentId === c.id;
          return (
            <div
              key={c.id}
              className={`anno-pin ${c.status === "resolved" ? "resolved" : ""}`}
              style={{ left: `${c.annotation.x}%`, top: `${c.annotation.y}%`, outline: isSel ? "2px solid white" : undefined }}
              onClick={(e) => { e.stopPropagation(); setSelectedCommentId && setSelectedCommentId(c.id); }}
              data-testid={`${testidPrefix}-pin-${c.id}`}
            >
              {num}
            </div>
          );
        })}
        {/* Pending (ghost) pin while comment is being typed */}
        {pendingAnno?.type === "pin" && (
          <div
            className="anno-pin pending"
            style={{ left: `${pendingAnno.x}%`, top: `${pendingAnno.y}%` }}
            data-testid={`${testidPrefix}-pin-pending`}
          >
            +
          </div>
        )}
      </div>

      {/* Controls + Timeline */}
      <div className="mt-3 px-1" data-testid="video-controls">
        <div className="flex items-center gap-3 mb-2">
          <Button size="sm" variant="outline" onClick={togglePlay} className="rounded-none h-8 w-8 p-0" data-testid="video-playpause-btn">
            {playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
          </Button>
          <span className="text-xs font-mono tabular-nums" data-testid="video-time-display">
            {fmtTime(currentTime)} / {fmtTime(duration)}
          </span>
          <select
            className="ml-auto text-xs font-mono border border-border h-8 bg-background px-2"
            value={rate}
            onChange={(e) => setRate(parseFloat(e.target.value))}
            data-testid="video-rate-select"
            title="Playback rate (saved across sessions)"
          >
            {[0.5, 0.75, 1, 1.25, 1.5, 2].map((r) => (
              <option key={r} value={r}>{r}x</option>
            ))}
          </select>
        </div>

        {/* Scrub bar with comment-pin markers */}
        <div
          className="relative h-6 bg-muted cursor-pointer"
          onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const pct = (e.clientX - rect.left) / rect.width;
            seekTo(pct * (duration || 0));
          }}
          data-testid="video-scrub-bar"
        >
          {/* progress */}
          <div
            className="absolute inset-y-0 left-0 bg-primary/30"
            style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
          />
          {/* playhead */}
          <div
            className="absolute inset-y-0 w-px bg-primary"
            style={{ left: `${duration ? (currentTime / duration) * 100 : 0}%` }}
          />
          {/* comment pins on the timeline */}
          {pinComments.map((c) => {
            const t = c.annotation.t || 0;
            const left = duration ? (t / duration) * 100 : 0;
            return (
              <div
                key={c.id}
                className="absolute top-0 bottom-0 flex items-center justify-center cursor-pointer"
                style={{ left: `calc(${left}% - 9px)`, width: 18 }}
                onClick={(e) => { e.stopPropagation(); seekTo(t); setSelectedCommentId && setSelectedCommentId(c.id); }}
                title={`#${comments.indexOf(c) + 1} at ${fmtTime(t)}: ${c.body.slice(0, 60)}`}
                data-testid={`video-timeline-pin-${c.id}`}
              >
                <MapPin className="w-3.5 h-3.5 text-primary" fill="currentColor" />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function fmtTime(s) {
  if (!isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${m}:${r.toString().padStart(2, "0")}`;
}
